/* eslint-disable react/jsx-props-no-spreading */
import { generatePath, useRoutes } from '@folklore/routes';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Menu } from '@micromag/core/components';
import { useUrlGenerator } from '@micromag/core/contexts';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'wouter';

import { useAuth as useAuthContext } from '../../contexts/AuthContext';
import { useOrganisation as useOrganisationContext } from '../../contexts/OrganisationContext';
import Avatar from '../partials/Avatar';

import styles from '../../../styles/menus/main.module.scss';

const propTypes = {
    locale: PropTypes.string,
    className: PropTypes.string,
    itemClassName: PropTypes.string,
    linkClassName: PropTypes.string,
    dropdownClassName: PropTypes.string,
};

const defaultProps = {
    locale: 'fr',
    className: null,
    itemClassName: null,
    linkClassName: null,
    dropdownClassName: null,
};

const MainMenu = ({
    locale,
    className,
    itemClassName,
    linkClassName,
    dropdownClassName,
    ...props
}) => {
    const routes = useRoutes();
    const url = useUrlGenerator();
    const [pathname] = useLocation();
    const { logout, user } = useAuthContext();
    const organisation = useOrganisationContext() || null;
    const { organisations = [] } = user;

    const onClickLogout = useCallback(
        (e) => {
            e.preventDefault();
            logout();
        },
        [logout],
    );

    const organisationsItems =
        organisations !== null
            ? organisations
                  .sort((it) => (organisation !== null && organisation.id === it.id ? -1 : 1))
                  .map((it) => ({
                      id: it.id,
                      href: url('organisations.switch', {
                          organisation: it.slug,
                      }),
                      external: true,
                      target: null,
                      label:
                          organisation !== null && organisation.id === it.id ? (
                              <strong>
                                  <FontAwesomeIcon icon={faCheck} className="me-1" />
                                  {it.name}
                              </strong>
                          ) : (
                              it.name
                          ),
                  }))
            : [];

    const finalOrganisationItems =
        organisationsItems.length > 0
            ? [
                  ...(organisation !== null
                      ? [
                            {
                                id: 'home',
                                href: url('home'),
                                label: (
                                    <FormattedMessage
                                        defaultMessage="Micromags"
                                        description="Menu item"
                                    />
                                ),
                            },
                            {
                                id: 'themes',
                                href: url('organisations.themes'),
                                label: (
                                    <FormattedMessage
                                        defaultMessage="Themes"
                                        description="Menu item"
                                    />
                                ),
                            },
                            {
                                id: 'medias',
                                href: url('medias.index'),
                                label: (
                                    <FormattedMessage
                                        defaultMessage="Media"
                                        description="Menu item"
                                    />
                                ),
                            },
                            {
                                id: 'settings',
                                href: url('organisations.settings'),
                                label: (
                                    <FormattedMessage
                                        defaultMessage="Settings"
                                        description="Menu item"
                                    />
                                ),
                            },
                            {
                                type: 'divider',
                                label: null,
                            },
                        ]
                      : []),
                  ...organisationsItems,
                  {
                      type: 'divider',
                      label: null,
                  },
              ]
            : [];

    const items = [
        {
            id: 'home',
            href: url('home'),
            label: user ? (
                <div className="d-inline-block">
                    <Avatar {...user} />
                </div>
            ) : (
                <FormattedMessage defaultMessage="Account" description="Menu item" />
            ),
            dropdown: [
                ...finalOrganisationItems,
                {
                    id: 'create',
                    href: url('organisations.create'),
                    external: organisation !== null,
                    target: null,
                    label: (
                        <FormattedMessage
                            defaultMessage="Create an organisation"
                            description="Menu item"
                        />
                    ),
                },
                {
                    type: 'divider',
                    label: null,
                },
                {
                    id: 'home',
                    href: url('user.home'),
                    external: organisation !== null,
                    target: null,
                    label: (
                        <FormattedMessage defaultMessage="My Micromags" description="Menu item" />
                    ),
                },
                {
                    id: 'account',
                    href: url('account'),
                    label: <FormattedMessage defaultMessage="Profile" description="Menu item" />,
                },
                {
                    id: 'locale',
                    href: `${pathname}?locale=${locale === 'fr' ? 'en' : 'fr'}`,
                    external: true,
                    target: '_self',
                    label:
                        locale === 'fr' ? (
                            <FormattedMessage
                                defaultMessage="English"
                                description="Locale english"
                            />
                        ) : (
                            <FormattedMessage
                                defaultMessage="Français"
                                description="Locale french"
                            />
                        ),
                    active: false,
                },
                {
                    id: 'logout',
                    href: url('auth.logout'),
                    label: <FormattedMessage defaultMessage="Logout" description="Menu item" />,
                    onClick: onClickLogout,
                },
            ],
        },
    ]
        .filter((it) => it !== null)
        .map((it) =>
            it.href === pathname
                ? {
                      ...it,
                      active: true,
                  }
                : it,
        );

    return (
        <Menu
            {...props}
            items={items}
            className={classNames([
                styles.container,
                {
                    [className]: className !== null,
                },
            ])}
            itemClassName={itemClassName}
            linkClassName={linkClassName}
            dropdownClassName={dropdownClassName}
        />
    );
};

MainMenu.propTypes = propTypes;
MainMenu.defaultProps = defaultProps;

export default MainMenu;
